// GEL BÊTA le 07/10/2026. Voir docs/GEL_BETA.md.
// Architecture figée : paiement dual, Coco via settings, rôles user_roles/has_role, âge 18 soft, pas de Connect.
// Porte ouverte : corrections de retours testeurs uniquement sur ordre Hiti, lot unique, sans rouvrir ces règles.
// Interdit sans Hiti : tama-wip→main, édition client.ts / types.ts / .env / config.toml / previewAuthStorage.
import { useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import VideoPlayer from "./VideoPlayer";
import AdBreak from "./AdBreak";
import { Video } from "@/types/videos";
import FeedDeck from "./FeedDeck";
import { filterValidVideos } from "@/lib/utils";
import {
  fetchVideoPage,
  takePrefetchedFirstPage,
  VideoPageResult,
} from "@/services/feedPrefetch";
import {
  AdItem,
  countVideoOnly,
  interleaveAds,
  isAdItem,
  prefetchAd,
  primeAds,
} from "@/services/ads";



type FeedItem = Video | AdItem;

/** Distance (en vidéos) avant la fin du feed déclenchant le chargement suivant. */

const PREFETCH_THRESHOLD = 3;


interface VideoFeedProps {
  /** Feed figé (voile d'authentification affiché par-dessus). */
  interactionLocked?: boolean;
  /**
   * Vidéo d'amorce (arrivée par un lien de partage) : elle ouvre le feed,
   * puis viennent les boosts et le reste du classement habituel.
   */
  seedVideo?: Video | null;
  /**
   * Aperçu visiteur : liste figée (pas de pagination, pas de publicité).
   * Utilisée uniquement pour un visiteur non connecté arrivant par un lien.
   */
  guestPlaylist?: Video[] | null;
  /** Remonte l'index courant au parent (aperçu visiteur). */
  onIndexChange?: (index: number) => void;
}

const VideoFeed = ({
  interactionLocked = false,
  seedVideo = null,
  guestPlaylist = null,
  onIndexChange,
}: VideoFeedProps) => {

  const [videos, setVideos] = useState<FeedItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  /**
   * Fin du chrono pub, mémorisée par diapo (clé : id `ad-…`).
   * Tant qu'une pub courante n'a pas fini ses 10 s, le fil est verrouillé.
   */
  const [adSkipReady, setAdSkipReady] = useState<Record<string, boolean>>({});

  // Le défilement (scroll, swipe, direction de préchargement) est entièrement
  // délégué à FeedDeck : un seul gestionnaire pour tout le lecteur.


  // Pagination : nombre de vidéos réelles déjà récupérées et disponibilité d'une suite
  const loadedVideoCount = useRef(0);
  const hasMore = useRef(true);
  const isFetchingMore = useRef(false);
  // Amorçage de la file publicitaire : une seule fois, après la première page.
  const adsPrimed = useRef(false);
  /**
   * Index courant miroir : lu dans les abonnements temps réel sans les réabonner.
   * Sert à ne jamais décaler la diapositive regardée (le conteneur positionne
   * les diapositives par index, une insertion en tête décalerait la lecture).
   */
  const currentIndexRef = useRef(0);
  currentIndexRef.current = currentIndex;


  useEffect(() => {
    loadFirstPage();

    /**
     * Realtime non destructif : on patche la liste locale au lieu de recharger
     * toute la première page (ce qui cassait la position de scroll).
     * Rechargement complet uniquement en dernier recours (erreur de fetch).
     */
    const handleInsert = async (videoId: string) => {
      try {
        const { data } = await supabase
          .from("videos")
          .select(
            `
            *,
            profiles:user_id (
              id,
              full_name,
              username,
              avatar_url
            ),
            tags:video_tags (
              tag_id,
              tags (
                id,
                name,
                is_reserved,
                is_held
              )
            )
          `
          )
          .eq("id", videoId)
          .eq("is_published", true)
          .eq("moderation_status", "approved")
          .maybeSingle();

        if (!data) return;

        // Ignorer les vidéos Bunny non prêtes ET sans URL de repli
        if (
          data.provider === "bunny" &&
          data.processing_status !== "ready" &&
          !(data as any).legacy_video_url
        ) {
          return;
        }

        const videoWithTags = {
          ...data,
          tags: (data as any).tags?.map((vt: any) => vt.tags).filter(Boolean) || [],
        } as Video;

        const valid = filterValidVideos([videoWithTags]);
        if (valid.length === 0) return;

        // Insertion en tête UNIQUEMENT si l'utilisateur est déjà en haut du feed
        // et hors amorce par lien (la vidéo partagée doit rester en position 1).
        if (seedVideo || guestPlaylist) return;
        if (currentIndexRef.current !== 0) return;


        setVideos((prev) => {
          if (prev.some((item) => item.id === videoId)) return prev;
          countVideoOnly("home");
          return [valid[0], ...prev];
        });

      } catch {
        // Dernier recours : rechargement complet
        loadFirstPage();
      }
    };

    const channel = supabase
      .channel("videos-feed-changes")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "videos",
        },
        (payload) => {
          handleInsert((payload.new as { id: string }).id);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "videos",
        },
        (payload) => {
          const deletedId = (payload.old as { id: string }).id;
          setVideos((prev) => {
            const removedIndex = prev.findIndex((item) => item.id === deletedId);
            if (removedIndex === -1) return prev;
            // Une suppression avant la diapositive courante décale la liste :
            // on compense l'index pour rester sur la même vidéo.
            if (removedIndex < currentIndexRef.current) {
              setCurrentIndex((index) => Math.max(0, index - 1));
            }
            return prev.filter((item) => item.id !== deletedId);
          });
        }

      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "videos",
        },
        (payload) => {
          const updated = payload.new as Video;
          setVideos((prev) =>
            prev.map((item) =>
              item.id === updated.id && !("isAd" in item)
                ? ({ ...item, ...updated } as FeedItem)
                : item
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      // Pause all videos when component unmounts (user changes tab)
      const allVideos = document.querySelectorAll("video");
      allVideos.forEach((video) => {
        if (!video.paused) {
          video.pause();
        }
      });
    };
    // Rechargement quand l'amorce (lien de partage) ou l'aperçu visiteur change.
  }, [seedVideo?.id, guestPlaylist]);


  /** Insère les publicités selon la cadence réglée en administration (service ads). */
  const withAds = (pageVideos: Video[]): FeedItem[] => interleaveAds(pageVideos, "home");

  /** Remplit la file publicitaire au repos, jamais avant la première vidéo. */
  const scheduleAdPriming = () => {
    if (adsPrimed.current) return;
    adsPrimed.current = true;
    const run = () => void primeAds("home");
    const idle = (window as any).requestIdleCallback as ((cb: () => void) => void) | undefined;
    if (idle) idle(run);
    else window.setTimeout(run, 1500);
  };

  /** Écarte la vidéo d'amorce des pages suivantes (jamais deux fois la même). */
  const withoutSeed = (pageVideos: Video[]) =>
    seedVideo ? pageVideos.filter((v) => v.id !== seedVideo.id) : pageVideos;

  const applyFirstPage = (page: VideoPageResult) => {
    hasMore.current = page.hasMore;
    loadedVideoCount.current = page.videos.length;
    const rest = withoutSeed(page.videos);
    // La vidéo du lien ouvre le feed ; aucune publicité en première position.
    const items = seedVideo ? [seedVideo as FeedItem, ...withAds(rest)] : withAds(rest);
    // Affichage immédiat : le lecteur se monte sans attendre tokens ni boosts.
    setVideos(items);
    setLoading(false);
    scheduleAdPriming();
  };

  const loadFirstPage = async () => {
    hasMore.current = true;

    // Aperçu visiteur : liste figée, ni pagination ni publicité.
    if (guestPlaylist) {
      hasMore.current = false;
      loadedVideoCount.current = guestPlaylist.length;
      setVideos(guestPlaylist);
      setLoading(false);
      return;
    }

    // Page déjà préparée pendant l'écran d'authentification : on la réutilise.
    const prefetched = takePrefetchedFirstPage();
    if (prefetched) {
      try {
        applyFirstPage(await prefetched);
        return;
      } catch {
        // Repli sur une requête classique ci-dessous.
      }
    }

    // Un échec réseau ne doit jamais laisser le feed sur un écran blanc :
    // on retombe sur l'état « Bienvenue ».
    try {
      applyFirstPage(await fetchVideoPage(0));
    } catch {
      hasMore.current = false;
      loadedVideoCount.current = 0;
      setVideos(seedVideo ? [seedVideo] : []);
      setLoading(false);
    }
  };

  const loadMore = useCallback(async () => {
    if (isFetchingMore.current || !hasMore.current) return;
    isFetchingMore.current = true;
    try {
      const page = await fetchVideoPage(loadedVideoCount.current);
      hasMore.current = page.hasMore;
      if (page.videos.length > 0) {
        loadedVideoCount.current += page.videos.length;
        const rest = withoutSeed(page.videos);
        // La page arrive déjà triée (boost → engagement → date) depuis la base.
        setVideos((prev) => [...prev, ...withAds(rest)]);
      }
    } finally {
      isFetchingMore.current = false;
    }
  }, [seedVideo?.id]);



  // Chargement incrémental lorsque l'utilisateur approche de la fin du feed
  useEffect(() => {
    if (videos.length > 0 && currentIndex >= videos.length - PREFETCH_THRESHOLD) {
      loadMore();
    }
  }, [currentIndex, videos.length, loadMore]);

  // Préchargement de la création dès qu'une carte publicitaire est à deux cartes ou moins.
  useEffect(() => {
    for (let i = currentIndex; i <= currentIndex + 2 && i < videos.length; i++) {
      const item = videos[i];
      if (isAdItem(item)) prefetchAd(item.ad);
    }
  }, [currentIndex, videos]);




  if (loading) {
    return (
      <div className="app-screen flex items-center justify-center bg-player-background">
        <div className="inline-block animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="app-screen flex items-center justify-center bg-player-background px-4">
        <div className="text-center">
          <p className="text-lg font-medium mb-2 text-player-foreground">Bienvenue sur Tama !</p>
          <p className="text-sm text-player-muted-foreground">
            Les vidéos apparaîtront ici une fois que les utilisateurs commenceront à publier
          </p>
        </div>
      </div>
    );
  }

  return (
    <FeedDeck
      videos={videos}
      initialIndex={0}
      onIndexChange={(index) => {
        setCurrentIndex(index);
        onIndexChange?.(index);
      }}

      isScrollLocked={
        isCommentsOpen ||
        interactionLocked ||
        (() => {
          // Pub affichée avec chrono en cours : le swipe reste verrouillé
          // jusqu'à la fin des 10 s (bouton « Continuer » ou swipe ensuite).
          const current = videos[currentIndex];
          return !!current && "isAd" in current && current.isAd && !adSkipReady[current.id];
        })()
      }
      renderVideo={(item, index, isActive, scrollToNext, totalVideos) => {
        if ("isAd" in item && item.isAd) {
          return (
            <AdBreak
              ad={(item as AdItem).ad}
              onComplete={scrollToNext}
              isActive={isActive}
              onCanSkipChange={(canSkip) =>
                setAdSkipReady((prev) =>
                  prev[item.id] === canSkip ? prev : { ...prev, [item.id]: canSkip }
                )
              }
            />
          );
        }
        return (
          <VideoPlayer
            video={item as Video}
            onRefresh={loadFirstPage}
            isActive={isActive}
            onCommentsOpenChange={setIsCommentsOpen}
            index={index}
            totalVideos={totalVideos}
          />
        );
      }}
      containerClassName="app-screen"
      videoClassName="relative"
    />
  );

};

export default VideoFeed;
